import { useQuery } from "@tanstack/react-query";
import { Globe, Radar, FileSearch, HelpCircle, Zap } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { PageHeader } from "@/admin/ui";

const Stat = ({ icon: Icon, label, value, sub, testid }: any) => (
  <div className="bg-[#121212] border border-white/10 rounded-xl p-5" data-testid={testid}>
    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">
      <Icon className="h-3.5 w-3.5 text-[#00E5FF]" /> {label}
    </p>
    <p className="font-mono text-3xl font-bold text-white mt-2">{value}</p>
    {sub && <p className="font-mono text-[11px] text-gray-500 mt-1">{sub}</p>}
  </div>
);

const PingStatus = ({ code }: { code: number | string | null }) => {
  if (code == null) return <span className="text-gray-600">—</span>;
  const ok = typeof code === "number" && code >= 200 && code < 300;
  return <span className={ok ? "text-green-400" : "text-red-400"}>{code}</span>;
};

export default function SeoHealth() {
  const { data } = useQuery({
    queryKey: ["admin-seo-health"],
    queryFn: async () => (await api.get("/admin/seo-health")).data,
    refetchInterval: 60000,
  });

  const s = data?.sitemap;
  const p = data?.pings;

  return (
    <div data-testid="admin-seo-health-page">
      <PageHeader title="SEO Health" sub="Indexing pings, sitemap coverage and where search engines land on your site" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={FileSearch} label="Sitemap URLs" value={s?.published_total ?? "—"}
          sub={`${s?.published_24h ?? 0} published in last 24h`} testid="seo-stat-sitemap" />
        <Stat icon={Radar} label="News sitemap (48h)" value={s?.news_sitemap_48h ?? "—"}
          sub="fresh articles Google can fast-crawl" testid="seo-stat-news" />
        <Stat icon={HelpCircle} label="FAQ coverage" value={s ? `${Math.round((s.faq_coverage / Math.max(1, s.published_total)) * 100)}%` : "—"}
          sub={s ? `${s.faq_coverage} of ${s.published_total} articles` : ""} testid="seo-stat-faq" />
        <Stat icon={Zap} label="Index pings (24h)" value={p?.count_24h ?? "—"}
          sub={`${p?.urls_24h ?? 0} URLs pushed · IndexNow ${p?.indexnow_configured ? "active" : "NOT configured"}`}
          testid="seo-stat-pings" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <div className="bg-[#121212] border border-white/10 rounded-xl p-5" data-testid="seo-recent-pings">
          <h2 className="font-heading font-bold text-white text-base mb-1">Recent indexing pings</h2>
          <p className="font-mono text-[11px] text-gray-500 mb-4">
            IndexNow → Bing, Yandex, Naver &amp; AI search · WebSub → Google feed hub. Google itself crawls via the news sitemap in Search Console.
          </p>
          {(p?.recent || []).length === 0 ? (
            <p className="font-mono text-xs text-gray-600 py-6">No pings yet — they fire automatically when articles publish.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-widest text-gray-500 text-left">
                  <th className="pb-2">When</th><th className="pb-2">URLs</th>
                  <th className="pb-2">IndexNow</th><th className="pb-2">WebSub</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {p.recent.map((r: any, i: number) => (
                  <tr key={i} className="font-mono text-xs">
                    <td className="py-2 text-gray-400">{timeAgo(r.created_at)}</td>
                    <td className="py-2 text-white">{r.urls}</td>
                    <td className="py-2"><PingStatus code={r.indexnow_status} /></td>
                    <td className="py-2"><PingStatus code={r.websub_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-[#121212] border border-white/10 rounded-xl p-5" data-testid="seo-search-landing">
          <h2 className="font-heading font-bold text-white text-base mb-1">Top search landing pages (7d)</h2>
          <p className="font-mono text-[11px] text-gray-500 mb-4">
            Visitors who arrived from a search engine, by the page they landed on.
          </p>
          {(data?.search_landing || []).length === 0 ? (
            <p className="font-mono text-xs text-gray-600 py-6">
              No search-referred visits recorded yet — data accrues as Google/Bing start sending readers.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-[10px] uppercase tracking-widest text-gray-500 text-left">
                  <th className="pb-2">Landing page</th><th className="pb-2">Visits</th><th className="pb-2">Engines</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {data.search_landing.map((l: any) => (
                  <tr key={l.path} className="font-mono text-xs">
                    <td className="py-2 text-white max-w-[240px] truncate">{l.path}</td>
                    <td className="py-2 text-[#00E5FF]">{l.visits}</td>
                    <td className="py-2 text-gray-400">{(l.engines || []).join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="mt-6 bg-[#0d1117] border border-[#00E5FF]/20 rounded-xl p-5" data-testid="seo-links">
        <h3 className="flex items-center gap-2 font-heading font-bold text-white text-sm mb-3">
          <Globe className="h-4 w-4 text-[#00E5FF]" /> Live SEO endpoints
        </h3>
        <div className="flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs">
          <a href="/api/v1/sitemap.xml" target="_blank" rel="noreferrer" className="text-[#00E5FF] hover:underline">sitemap.xml</a>
          <a href="/api/v1/news-sitemap.xml" target="_blank" rel="noreferrer" className="text-[#00E5FF] hover:underline">news-sitemap.xml</a>
          <a href="/api/v1/feed.xml" target="_blank" rel="noreferrer" className="text-[#00E5FF] hover:underline">feed.xml (RSS)</a>
          <a href="/robots.txt" target="_blank" rel="noreferrer" className="text-[#00E5FF] hover:underline">robots.txt</a>
        </div>
      </div>
    </div>
  );
}
